import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { Icon } from './icon'
import { Popover } from './popover'
import { useColumnConfigs, useSaveColumnConfig, type ColumnConfig } from '../lib/api/column-configs'
import { useMe } from '../lib/use-me'

export type ColumnDef<Row> = {
  id: string
  header: ReactNode
  /** Short label for the visibility menu (defaults to `header` when it is a string). */
  menuLabel?: string
  width?: number
  thClassName?: string
  cell: (row: Row) => ReactNode
  cellClassName?: string
  /** Pinned columns render last and can't be reordered or hidden (e.g. actions, sticky balance). */
  pinned?: boolean
  /**
   * Makes the column sortable: clicking its header sorts the loaded rows by this
   * value (numbers numerically, otherwise locale string compare; nullish last).
   * Omit to leave the column unsortable.
   */
  sortValue?: (row: Row) => string | number | null | undefined
}

type SortState = { id: string; dir: 'asc' | 'desc' }

/** Compares two sort values: numbers numerically, otherwise by locale string; nullish sorts last. */
function compareSortValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number {
  const aMissing = a == null || a === ''
  const bMissing = b == null || b === ''
  if (aMissing && bMissing) return 0
  if (aMissing) return 1
  if (bMissing) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b))
}

/** Returns a new array sorted by the active column; the input is left untouched. */
function sortRows<Row>(rows: Row[], columns: ColumnDef<Row>[], sort: SortState | null): Row[] {
  if (!sort) return rows
  const col = columns.find((c) => c.id === sort.id)
  if (!col?.sortValue) return rows
  const dir = sort.dir === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => dir * compareSortValues(col.sortValue!(a), col.sortValue!(b)))
}

/** Cycles a column's sort: off → asc → desc → off. */
function nextSort(current: SortState | null, id: string): SortState | null {
  if (current?.id !== id) return { id, dir: 'asc' }
  if (current.dir === 'asc') return { id, dir: 'desc' }
  return null
}

/**
 * Returns the columns to render: movable columns ordered by `order` (unknown ids
 * appended in their declared order, hidden ones dropped) followed by pinned columns.
 * Pure — unit tested.
 */
export function resolveColumns<Row>(
  columns: ColumnDef<Row>[],
  order: string[],
  hidden: string[],
): ColumnDef<Row>[] {
  const movable = columns.filter((c) => !c.pinned)
  const pinned = columns.filter((c) => c.pinned)
  const byId = new Map(movable.map((c) => [c.id, c]))
  const orderedIds = [
    ...order.filter((id) => byId.has(id)),
    ...movable.filter((c) => !order.includes(c.id)).map((c) => c.id),
  ]
  const hiddenSet = new Set(hidden)
  const visible = orderedIds.filter((id) => !hiddenSet.has(id)).map((id) => byId.get(id)!)
  return [...visible, ...pinned]
}

/** Moves `fromId` to sit just before `toId` within the order list. Pure — unit tested. */
export function reorderColumns(order: string[], fromId: string, toId: string): string[] {
  if (fromId === toId) return order
  const out = order.filter((id) => id !== fromId)
  const idx = out.indexOf(toId)
  out.splice(idx < 0 ? out.length : idx, 0, fromId)
  return out
}

function movableOrder<Row>(columns: ColumnDef<Row>[], saved?: ColumnConfig): string[] {
  const movableIds = columns.filter((c) => !c.pinned).map((c) => c.id)
  if (!saved) return movableIds
  return [...saved.order.filter((id) => movableIds.includes(id)), ...movableIds.filter((id) => !saved.order.includes(id))]
}

const menuLabelOf = <Row,>(c: ColumnDef<Row>): string =>
  c.menuLabel ?? (typeof c.header === 'string' ? c.header : c.id)

type Props<Row> = {
  tableKey: string
  columns: ColumnDef<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string
  onRowClick?: (row: Row) => void
  rowClassName?: (row: Row) => string | undefined
  rowStyle?: (row: Row) => CSSProperties | undefined
  className?: string
  /** Rendered as a full-width row when there are no rows. */
  empty?: ReactNode
}

/**
 * A `<table className="tbl">` whose column order and visibility are admin-configurable
 * and persisted app-wide (keyed by `tableKey`). Admins reorder via native drag on the
 * header cells and toggle visibility via the ⚙ menu; everyone else sees the saved layout.
 */
export function DataTable<Row>({
  tableKey,
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  rowStyle,
  className = 'tbl',
  empty,
}: Props<Row>) {
  const me = useMe()
  const isAdmin = me.data?.role === 'admin'
  const configs = useColumnConfigs()
  const save = useSaveColumnConfig()
  const saved = configs.data?.configs[tableKey]

  // Local working copy so reorder/hide/resize feel instant; seeded from the server config.
  const [order, setOrder] = useState<string[]>(() => movableOrder(columns, saved))
  const [hidden, setHidden] = useState<string[]>(() => saved?.hidden ?? [])
  const [widths, setWidths] = useState<Record<string, number>>(() => saved?.widths ?? {})
  const [dragId, setDragId] = useState<string | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  // Sort is session-local UI state (not persisted): clicking a sortable header
  // sorts the loaded rows. Available to everyone, not just admins.
  const [sort, setSort] = useState<SortState | null>(null)
  // Set while dragging a resize handle so the th's native drag-reorder doesn't fire.
  const suppressDrag = useRef(false)

  useEffect(() => {
    setOrder(movableOrder(columns, saved))
    setHidden(saved?.hidden ?? [])
    setWidths(saved?.widths ?? {})
    // Re-sync whenever the server config for this table changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved, tableKey])

  const persist = (nextOrder: string[], nextHidden: string[], nextWidths: Record<string, number> = widths) => {
    setOrder(nextOrder)
    setHidden(nextHidden)
    setWidths(nextWidths)
    save.mutate({ tableKey, config: { order: nextOrder, hidden: nextHidden, widths: nextWidths } })
  }

  const display = resolveColumns(columns, order, hidden)
  const movable = columns.filter((c) => !c.pinned)
  const hiddenSet = new Set(hidden)
  const sortedRows = sortRows(rows, columns, sort)

  const handleDrop = (toId: string) => {
    if (!dragId || !isAdmin) return
    persist(reorderColumns(order, dragId, toId), hidden)
    setDragId(null)
  }

  const toggleHidden = (id: string) => {
    const next = hiddenSet.has(id) ? hidden.filter((h) => h !== id) : [...hidden, id]
    persist(order, next)
  }

  // Drag the right edge of a header cell to resize that column. Live-updates the
  // local width on move; persists once on release. Admin-only, global (same as
  // order/visibility).
  const startResize = (e: ReactPointerEvent, id: string, currentWidth: number) => {
    e.preventDefault()
    e.stopPropagation()
    suppressDrag.current = true
    const startX = e.clientX
    let finalWidth = currentWidth
    const onMove = (ev: PointerEvent) => {
      finalWidth = Math.max(40, Math.min(1000, currentWidth + (ev.clientX - startX)))
      setWidths((prev) => ({ ...prev, [id]: finalWidth }))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      suppressDrag.current = false
      persist(order, hidden, { ...widths, [id]: finalWidth })
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  return (
    <>
      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 8px 0' }}>
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              title="Налаштувати колонки"
              aria-label="Налаштувати колонки"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                color: 'var(--fg-3)',
                fontSize: 11,
                fontFamily: 'inherit',
              }}
            >
              <Icon name="settings-2" size={13} />
              Колонки
            </button>
            <Popover open={menuOpen} onClose={() => setMenuOpen(false)} width={220} align="right">
              <div style={{ padding: 6 }}>
                <div
                  style={{
                    fontSize: 10,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: 'var(--fg-4)',
                    padding: '4px 8px 6px',
                  }}
                >
                  Видимі колонки
                </div>
                {movable.map((c) => (
                  <label
                    key={c.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 8px',
                      fontSize: 12,
                      cursor: 'pointer',
                      color: 'var(--fg-1)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!hiddenSet.has(c.id)}
                      onChange={() => toggleHidden(c.id)}
                    />
                    {menuLabelOf(c)}
                  </label>
                ))}
              </div>
            </Popover>
          </span>
        </div>
      )}
      <div className="tbl-scroll">
      <table className={className}>
        <thead>
          <tr>
            {display.map((c) => {
              const draggable = isAdmin && !c.pinned
              const colWidth = widths[c.id] ?? c.width
              const sortable = !!c.sortValue
              const sortDir = sort?.id === c.id ? sort.dir : null
              return (
                <th
                  key={c.id}
                  className={c.thClassName}
                  style={{
                    width: colWidth,
                    position: 'relative',
                    cursor: sortable ? 'pointer' : draggable ? 'grab' : undefined,
                    opacity: dragId === c.id ? 0.5 : undefined,
                  }}
                  draggable={draggable}
                  onDragStart={
                    draggable
                      ? (e) => {
                          // A resize drag must not start a column-reorder drag.
                          if (suppressDrag.current) {
                            e.preventDefault()
                            return
                          }
                          setDragId(c.id)
                        }
                      : undefined
                  }
                  onDragOver={draggable ? (e) => e.preventDefault() : undefined}
                  onDrop={draggable ? () => handleDrop(c.id) : undefined}
                  onDragEnd={draggable ? () => setDragId(null) : undefined}
                  onClick={sortable ? () => setSort((s) => nextSort(s, c.id)) : undefined}
                  aria-sort={
                    sortDir ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                >
                  {c.header}
                  {sortable && (
                    <span className="col-sort" aria-hidden>
                      {sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}
                    </span>
                  )}
                  {isAdmin && (
                    <span
                      className="col-resize"
                      onPointerDown={(e) => startResize(e, c.id, colWidth ?? 120)}
                      onClick={(e) => e.stopPropagation()}
                      aria-hidden
                    />
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && empty != null ? (
            <tr>
              <td colSpan={display.length} style={{ padding: 24, color: 'var(--fg-3)' }}>
                {empty}
              </td>
            </tr>
          ) : (
            sortedRows.map((row) => (
              <tr
                key={rowKey(row)}
                className={rowClassName?.(row)}
                style={{ cursor: onRowClick ? 'pointer' : undefined, ...rowStyle?.(row) }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {display.map((c) => (
                  <td key={c.id} className={c.cellClassName}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </>
  )
}
